import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X } from 'lucide-react';
import { useNotesStore } from '../stores/notesStore';
import type { BibleVerse } from '../lib/types';

interface NoteModalProps {
  isOpen: boolean;
  onClose: () => void;
  verse: BibleVerse;
}

export function NoteModal({ isOpen, onClose, verse }: NoteModalProps) {
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const { getNote, addNote, updateNote } = useNotesStore();

  // Format verse_id consistently
  const verseId = `${verse.book.toLowerCase()}_${verse.chapter}_${verse.verse}`;

  // Load existing note
  useEffect(() => {
    let mounted = true;

    const loadNote = async () => {
      if (!isOpen) return;

      try {
        const existingNote = await getNote(verseId);
        if (mounted && existingNote) {
          setNote(existingNote.content);
        } else if (mounted) {
          setNote('');
        }
      } catch (error) {
        console.error('Error loading existing note:', error);
        if (mounted) {
          setNote('');
        }
      }
    };

    loadNote();
    return () => {
      mounted = false;
    };
  }, [isOpen, verseId, getNote]);

  // Handle modal focus and scroll lock
  useEffect(() => {
    if (isOpen) {
      // Prevent body scrolling when modal is open
      document.body.style.overflow = 'hidden';
      // Focus the textarea
      textareaRef.current?.focus();
    }
    return () => {
      // Re-enable body scrolling when modal is closed
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Save on Ctrl/Cmd + Enter
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!note.trim() || loading) return;

    setLoading(true);
    try {
      const existingNote = await getNote(verseId);
      if (existingNote) {
        await updateNote(verseId, note.trim());
      } else {
        await addNote(verseId, note.trim());
      }
      onClose();
    } catch (error) {
      console.error('Error saving note:', error);
      alert('Failed to save note. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-start justify-center z-[9999] p-4 sm:p-6 md:p-8">
      <div 
        className="bg-gray-900 rounded-lg p-6 w-full max-w-lg border border-white/10 relative mt-[10vh]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-white">
            Add Note to {verse.reference}
          </h3>
          <button
            onClick={onClose}
            disabled={loading}
            className="text-white/60 hover:text-white transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <textarea
            ref={textareaRef}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Write your note here..."
            className="w-full h-48 p-3 bg-black/20 border border-white/10 rounded-lg text-white/90 placeholder:text-white/40 focus:outline-none focus:border-gold-500/50"
            disabled={loading}
          />
          <div className="flex justify-end gap-3 mt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="px-4 py-2 text-sm text-white/60 hover:text-white transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !note.trim()}
              className={`px-4 py-2 text-sm bg-gold-500 text-black font-medium rounded-lg hover:bg-gold-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                loading ? 'animate-pulse' : ''
              }`}
            >
              {loading ? 'Saving...' : 'Save Note'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
